// ─── src/GewFeedbackView.tsx ─────────────────────────────────────────────────
//
// GEW 2026 attendee feedback, for staff:
//   * Overall: KPIs and a chart per rating question across every event.
//   * Every event in one sortable, filterable table; click a row to open
//     that event's charts and each answer with the respondent's name.
//   * Quotes attendees agreed to publish.
//   * CSV export (all responses, or one event's).
//   * Send the form for any event (same rules as the organiser's button).

import { useMemo, useState } from "react";
import axios from "axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Download, Quote, Search, Send } from "lucide-react";
import {
  getEventFeedbackAdmin,
  getFeedbackOverview,
  getFeedbackQuotes,
  getFeedbackResponses,
  sendFeedbackAdmin,
  type FeedbackEventRow,
  type FeedbackSendStatus,
  type FeedbackSource,
} from "./api/gewFeedback";
import {
  FeedbackCharts,
  FeedbackKpis,
  ResponsesList,
  exportFeedbackCsv,
} from "./components/gewFeedback/FeedbackCharts";
import { TableSkeleton } from "./components/ui/Skeleton";

type SortKey =
  | "eventDate" | "title" | "emailed" | "responses" | "responseRate"
  | "ratingOverall" | "ratingContent" | "ratingSpeakers" | "ratingOrganization" | "npsScore";

const COLUMNS: [SortKey, string][] = [
  ["title", "Event"],
  ["eventDate", "Date"],
  ["emailed", "Emailed"],
  ["responses", "Responses"],
  ["responseRate", "Rate"],
  ["ratingOverall", "Overall"],
  ["ratingContent", "Content"],
  ["ratingSpeakers", "Speakers"],
  ["ratingOrganization", "Organisation"],
  ["npsScore", "NPS"],
];

const today = () => new Date().toISOString().slice(0, 10);

export default function GewFeedbackView() {
  const [tab, setTab] = useState<"events" | "quotes">("events");
  const [selected, setSelected] = useState<{ sourceType: FeedbackSource; id: string } | null>(null);
  const [exporting, setExporting] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["gew-feedback", "overview"],
    queryFn: getFeedbackOverview,
    staleTime: 30_000,
  });

  const exportAll = async () => {
    setExporting(true);
    try {
      exportFeedbackCsv(await getFeedbackResponses(), `gew_feedback_all_${today()}.csv`);
    } finally {
      setExporting(false);
    }
  };

  if (selected) {
    return <EventDetail {...selected} onBack={() => setSelected(null)} />;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-white text-xl font-bold">GEW Feedback</h2>
          <p className="text-brand-muted text-sm">
            Attendee feedback for every GEW 2026 event: partner events and catalyst meetups.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setTab("events")}
            className={tab === "events" ? "btn-primary text-sm" : "btn-ghost text-sm"}
          >
            Events
          </button>
          <button
            onClick={() => setTab("quotes")}
            className={tab === "quotes" ? "btn-primary text-sm" : "btn-ghost text-sm"}
          >
            Quotes
          </button>
          <button onClick={exportAll} disabled={exporting} className="btn-ghost text-sm inline-flex items-center gap-1.5">
            <Download size={14} /> {exporting ? "Exporting…" : "Export all (CSV)"}
          </button>
        </div>
      </div>

      {isLoading && <TableSkeleton />}
      {isError && <div className="card p-6 text-red-400 text-sm">Couldn&rsquo;t load feedback.</div>}

      {data && tab === "events" && (
        <>
          <FeedbackKpis summary={data.summary} />
          <FeedbackCharts summary={data.summary} />
          {data.orphanResponses > 0 && (
            <p className="text-brand-muted text-xs">
              {data.orphanResponses} response{data.orphanResponses !== 1 ? "s" : ""} belong to events outside
              the GEW list (drafts, cancelled or unpublished) and are left out of the totals above.
            </p>
          )}
          <EventsTable rows={data.events} onOpen={(r) => setSelected({ sourceType: r.sourceType, id: r.id })} />
        </>
      )}

      {tab === "quotes" && <QuotesList />}
    </div>
  );
}

function EventsTable({ rows, onOpen }: { rows: FeedbackEventRow[]; onOpen: (r: FeedbackEventRow) => void }) {
  const [search, setSearch] = useState("");
  const [gov, setGov] = useState("");
  const [source, setSource] = useState<"" | FeedbackSource>("");
  const [onlyResponded, setOnlyResponded] = useState(false);
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: "eventDate", dir: 1 });

  const governorates = useMemo(
    () => Array.from(new Set(rows.map((r) => r.governorate).filter(Boolean) as string[])).sort(),
    [rows],
  );

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = rows.filter(
      (r) =>
        (!q || `${r.title} ${r.organizerName} ${r.city ?? ""}`.toLowerCase().includes(q)) &&
        (!gov || r.governorate === gov) &&
        (!source || r.sourceType === source) &&
        (!onlyResponded || r.responses > 0),
    );
    return list.sort((a, b) => {
      const x = a[sort.key];
      const y = b[sort.key];
      // Empty values always last, whatever the direction.
      if (x == null && y == null) return 0;
      if (x == null) return 1;
      if (y == null) return -1;
      return (typeof x === "number" ? x - (y as number) : String(x).localeCompare(String(y))) * sort.dir;
    });
  }, [rows, search, gov, source, onlyResponded, sort]);

  const toggleSort = (key: SortKey) =>
    setSort((s) => ({ key, dir: s.key === key ? (s.dir === 1 ? -1 : 1) : key === "title" || key === "eventDate" ? 1 : -1 }));

  return (
    <div className="card overflow-hidden">
      <div className="px-5 py-4 border-b border-brand-border flex flex-wrap items-center gap-3">
        <p className="text-white font-semibold text-sm mr-auto">
          Events ({shown.length}{shown.length !== rows.length ? ` of ${rows.length}` : ""})
        </p>
        <div className="relative">
          <Search size={13} className="absolute left-2.5 top-2.5 text-brand-muted" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Event, organiser, city…"
            className="bg-brand-navy border border-brand-border rounded-lg pl-8 pr-3 py-1.5 text-sm text-white"
          />
        </div>
        <select value={gov} onChange={(e) => setGov(e.target.value)}
          className="bg-brand-navy border border-brand-border rounded-lg px-3 py-1.5 text-sm text-white">
          <option value="">All governorates</option>
          {governorates.map((g) => <option key={g} value={g}>{g}</option>)}
        </select>
        <select value={source} onChange={(e) => setSource(e.target.value as "" | FeedbackSource)}
          className="bg-brand-navy border border-brand-border rounded-lg px-3 py-1.5 text-sm text-white">
          <option value="">Partner events + meetups</option>
          <option value="gew_event">Partner events</option>
          <option value="meetup">Catalyst meetups</option>
        </select>
        <label className="flex items-center gap-2 text-sm text-brand-muted">
          <input type="checkbox" checked={onlyResponded} onChange={(e) => setOnlyResponded(e.target.checked)} />
          With responses only
        </label>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-brand-border">
              {COLUMNS.map(([key, label]) => (
                <th key={key} className="px-4 py-3 text-left text-brand-muted font-medium whitespace-nowrap">
                  <button onClick={() => toggleSort(key)} className="hover:text-white">
                    {label}{sort.key === key ? (sort.dir === 1 ? " ▲" : " ▼") : ""}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => (
              <tr
                key={`${r.sourceType}:${r.id}`}
                onClick={() => onOpen(r)}
                className="border-b border-brand-border/50 hover:bg-brand-border/20 cursor-pointer transition-colors"
              >
                <td className="px-4 py-3 min-w-[260px]" dir="rtl">
                  <p className="text-white font-medium">{r.title}</p>
                  <p className="text-brand-muted text-xs">
                    {r.organizerName} · {r.city ?? r.governorate ?? "—"}
                    {r.sourceType === "meetup" ? " · ميتاب كتالست" : ""}
                  </p>
                </td>
                <td className="px-4 py-3 text-brand-muted whitespace-nowrap">{r.eventDate ?? "—"}</td>
                <td className="px-4 py-3 text-brand-muted">{r.emailed}</td>
                <td className="px-4 py-3 text-white font-semibold">{r.responses}</td>
                <td className="px-4 py-3 text-brand-muted">{r.responseRate == null ? "—" : `${r.responseRate}%`}</td>
                <Score v={r.ratingOverall} />
                <Score v={r.ratingContent} />
                <Score v={r.ratingSpeakers} />
                <Score v={r.ratingOrganization} />
                <td className="px-4 py-3 text-white">{r.npsScore ?? "—"}</td>
              </tr>
            ))}
            {shown.length === 0 && (
              <tr><td colSpan={COLUMNS.length} className="px-4 py-10 text-center text-brand-muted">No events match.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Score({ v }: { v: number | null }) {
  const color = v == null ? "text-brand-muted" : v >= 4 ? "text-emerald-400" : v >= 3 ? "text-amber-400" : "text-red-400";
  return <td className={`px-4 py-3 font-semibold ${color}`}>{v ?? "—"}</td>;
}

/** Staff send the form for any event; same rules as the organiser's button. */
function SendPanel({ sourceType, id, send: s }: { sourceType: FeedbackSource; id: string; send: FeedbackSendStatus }) {
  const qc = useQueryClient();
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);
  const send = useMutation({
    mutationFn: () => sendFeedbackAdmin(sourceType, id),
    onSuccess: (res) => {
      setNotice({ ok: true, text: res.message });
      qc.invalidateQueries({ queryKey: ["gew-feedback"] });
    },
    onError: (err) =>
      setNotice({
        ok: false,
        text:
          (axios.isAxiosError(err) && (err.response?.data as { message?: string })?.message) ||
          "Couldn't send. Please try again.",
      }),
  });
  const reason = !s.eventStarted
    ? "The form can be sent from the start of the event."
    : s.eligible === 0
      ? "The form only goes to people checked in at the door, and nobody has been checked in yet."
      : s.toSend === 0
        ? s.recentlySent
          ? "Everyone got the form in the last 24 hours. It can be resent to people who haven't answered after that."
          : "Every attendee has answered."
        : null;

  return (
    <div className="card p-5 space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-white text-sm font-semibold flex items-center gap-2">
            <Send size={15} className="text-brand-green" /> Send the feedback form
          </p>
          <p className="text-brand-muted text-xs mt-0.5">
            Emails each checked-in attendee their own link. Never goes to someone who already answered.
          </p>
        </div>
        <button
          disabled={!s.canSend || send.isPending}
          onClick={() => {
            setNotice(null);
            if (window.confirm(`Email the feedback form to ${s.toSend} attendee${s.toSend !== 1 ? "s" : ""}?`)) {
              send.mutate();
            }
          }}
          className="btn-primary text-sm shrink-0 inline-flex items-center gap-1.5 disabled:opacity-40"
        >
          <Send size={14} />
          {send.isPending ? "Sending…" : s.toSend > 0 ? `Send feedback form (${s.toSend})` : "Send feedback form"}
        </button>
      </div>
      <p className="text-brand-muted text-xs">
        Checked in: {s.eligible} · emailed: {s.emailed} · answered: {s.submitted}
        {s.lastSentAt ? ` · last sent ${new Date(s.lastSentAt).toLocaleString()}` : " · never sent"}
      </p>
      {reason && <p className="text-brand-muted text-xs">{reason}</p>}
      {notice && (
        <p
          className={`text-sm rounded-lg px-3 py-2 border ${
            notice.ok ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-400" : "border-red-500/40 bg-red-500/10 text-red-400"
          }`}
          dir="auto"
        >
          {notice.text}
        </p>
      )}
    </div>
  );
}

function EventDetail({ sourceType, id, onBack }: { sourceType: FeedbackSource; id: string; onBack: () => void }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["gew-feedback", "event", sourceType, id],
    queryFn: () => getEventFeedbackAdmin(sourceType, id),
  });
  const [exporting, setExporting] = useState(false);

  return (
    <div className="space-y-6">
      <button onClick={onBack} className="btn-ghost text-sm inline-flex items-center gap-1.5">
        <ArrowLeft size={14} /> All events
      </button>
      {isLoading && <TableSkeleton />}
      {isError && <div className="card p-6 text-red-400 text-sm">Couldn&rsquo;t load this event.</div>}
      {data && (
        <>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div dir="rtl" className="min-w-0">
              <h2 className="text-white text-xl font-bold">{data.event.title}</h2>
              <p className="text-brand-muted text-sm">
                {data.event.organizerName} · {data.event.eventDate ?? "—"} · {data.event.city ?? data.event.governorate ?? "—"}
                {sourceType === "meetup" ? " · ميتاب كتالست" : ""}
              </p>
            </div>
            <button
              disabled={exporting || data.responses.length === 0}
              onClick={async () => {
                setExporting(true);
                try {
                  exportFeedbackCsv(await getFeedbackResponses(sourceType, id), `gew_feedback_${id.slice(0, 8)}_${today()}.csv`);
                } finally {
                  setExporting(false);
                }
              }}
              className="btn-ghost text-sm inline-flex items-center gap-1.5 disabled:opacity-40"
            >
              <Download size={14} /> Export event (CSV)
            </button>
          </div>
          <SendPanel sourceType={sourceType} id={id} send={data.send} />
          <FeedbackKpis summary={data.summary} />
          <FeedbackCharts summary={data.summary} />
          <ResponsesList rows={data.responses} />
        </>
      )}
    </div>
  );
}

function QuotesList() {
  const { data, isLoading } = useQuery({ queryKey: ["gew-feedback", "quotes"], queryFn: getFeedbackQuotes });
  if (isLoading) return <TableSkeleton />;
  if (!data?.length) {
    return <div className="card p-10 text-center text-brand-muted">No quotes yet.</div>;
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {data.map((q) => (
        <div key={q.id} className="card p-5" dir="rtl">
          <Quote size={16} className="text-brand-green" />
          <p className="text-white mt-2 leading-relaxed">«{q.quote}»</p>
          <p className="text-brand-muted text-sm mt-3">
            {q.name ?? "—"} · التقييم {q.ratingOverall}/5
          </p>
          {q.event && (
            <p className="text-brand-green text-xs mt-1">
              {q.event.title} · {q.event.organizerName}{q.event.city ? ` · ${q.event.city}` : ""}
            </p>
          )}
        </div>
      ))}
    </div>
  );
}
